import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Grid, DialogTitle, DialogContent, Box, Typography } from '@mui/material';
import { TextField, IconButton, Badge, Select, MenuItem } from '@mui/material';
import { Autocomplete } from '@mui/material';
import { HeaderButton, PageContainer, RoundedDialog, StandardDialogActions } from '../../Global/Components';
import { AddRounded } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import PageHeader from '../../Global/PageHeader';
import { QnAIcon, ProcessIcon, FormIcon, AssetIcon, AuditIcon, PortalIcon, EditIcon } from '../../Global/Icons';
import { RegistersIcon, ResourcesIcon, KPIHubIcon, DeleteIcon, TaskIcon, DocIcon } from '../../Global/Icons';
import { Slide } from '@mui/material';
import { isEmpty } from 'lodash';
import axios from 'axios';

const hubs = {
  'Process hub': ProcessIcon,
  'Form hub': FormIcon,
  'Asset hub': AssetIcon,
  'Audit hub': AuditIcon,
  Portals: PortalIcon,
  'Work hub': TaskIcon,
  'Doc hub': DocIcon,
  'Register hub': RegistersIcon,
  Resources: ResourcesIcon,
  KPI: KPIHubIcon,
};

export default function QnASection({}) {
  const [searchPattern, setSearchPattern] = useState('');
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [initial, setInitial] = useState({});

  const { user } = useSelector(({ profile }) => profile);

  const isSuperAdmin = user?.isSuperAdmin;

  const standardColumns = [
    { field: 'title', headerName: 'Question', flex: 1 },
    {
      field: 'hub',
      headerName: 'Hub',
      renderCell: (row) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Typography>{row.row.hub}</Typography>
          </Box>
        </Box>
      ),
    },
    { field: 'type', headerName: 'Type' },
    { field: 'views', headerName: 'Views', width: 100 },
  ];

  const adminColumns = [
    {
      field: 'edit',
      headerName: '',
      renderCell: (row) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <IconButton
            onClick={() => {
              setInitial(row.row);
              setDialog(true);
            }}
          >
            <EditIcon />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'delete',
      headerName: '',
      renderCell: (row) => (
        <IconButton onClick={() => deleteQA(row.row._id)}>
          <DeleteIcon sx={{ color: 'red' }} />
        </IconButton>
      ),
    },
  ];

  useEffect(() => {
    axios.get('/help/getAnswerTemplates').then((data) => {
      setTemplates(data.data);
    });
  }, []);

  useEffect(() => {
    axios.get('/help/getQA', { params: { searchPattern } }).then((data) => {
      setAnswers(data.data);
    });
  }, [searchPattern]);

  const columns = [...standardColumns, ...(isSuperAdmin ? adminColumns : [])];

  const getDetailPanelContent = ({ row }) => (
    <Box sx={{ p: 3, width: '100%' }}>
      <Typography variant="body1">Answer: {row.template}</Typography>
      <Grid container item justifyContent={'flex-end'} alignItems={'center'} gap={2}>
        <Typography>If the answer helped you, click the trophy</Typography>
        <IconButton onClick={() => increaseHelpful(row)}>
          <Badge badgeContent={row?.findHelpful} color="primary">
            🏆
          </Badge>
        </IconButton>
      </Grid>
    </Box>
  );

  const getDetailPanelHeight = () => 'auto';

  const handleRowClick = (params) => {
    const rowId = params.id;

    const isExpanding = !expandedRowIds.includes(rowId);
    if (isExpanding) {
      params.row.views += 1;
      increaseViews(params.row.answerTemplateId);
      setAnswers((prev) =>
        prev.map((answer) => (answer._id === params.row._id ? { ...answer, views: params.row.views } : answer)),
      );
    }

    setExpandedRowIds((prev) => (prev.includes(rowId) ? [] : [rowId]));
  };

  function increaseViews(id) {
    axios.get('/help/increaseAnswerView', { params: { id } });
  }

  function increaseHelpful(row) {
    const id = row.answerTemplateId;
    row.findHelpful += 1;
    setAnswers((prev) =>
      prev.map((answer) => (answer._id === row._id ? { ...answer, findHelpful: row.findHelpful } : answer)),
    );
    axios.get('/help/increaseHelpful', { params: { id } }).then();
  }

  function saveQnA(data) {
    axios.post('/help/saveQA', data).then((res) => {
      const templateData = templates.find((template) => res.data.answerTemplateId === template._id);
      const answer = { ...templateData, ...res.data };
      if (data._id) {
        setAnswers((prev) => prev.map((item) => (item._id === data._id ? { ...item, ...answer } : item)));
      } else {
        setAnswers([...answers, answer]);
      }

      setInitial({});
    });
  }

  function deleteQA(qaId) {
    axios.post('/help/deleteQA', { qaId }).then((res) => {
      setAnswers((prev) => prev.filter((p) => p._id !== qaId));
    });
  }

  return (
    <PageContainer container sx={{ height: '100%' }} direction="column" alignItems="flex-start">
      <PageHeader
        title={'QA'}
        onSearch={setSearchPattern}
        sx={{ mt: 0, px: 1 }}
        icon={<QnAIcon />}
        options={
          isSuperAdmin ? (
            <HeaderButton
              color="primary"
              text="Add QA"
              icon={<AddRounded fontSize="large" />}
              onClick={() => {
                setDialog(true);
              }}
            />
          ) : null
        }
      />
      <Grid item container sx={{ flex: 1, position: 'relative' }}>
        <Grid item container sx={{ position: 'absolute', height: '100%' }}>
          <DataGridPremium
            rows={answers}
            columns={columns}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            detailPanelExpandedRowIds={expandedRowIds}
            getRowId={(row) => row._id}
            onRowClick={handleRowClick}
            disableRowSelectionOnClick
            sx={{ borderRadius: '10px', mt: 1 }}
          />
        </Grid>
      </Grid>
      <EditQADialog
        init={initial}
        open={dialog}
        onClose={() => {
          setDialog(false);
          setInitial({});
        }}
        templates={templates}
        onSave={(data) => saveQnA(data)}
        onEdit={(data) => saveQnA(data)}
      />
    </PageContainer>
  );
}

function EditQADialog({ init = {}, open, onClose, onSave, onEdit, templates = [] }) {
  const isEditMode = !isEmpty(init);

  const [formData, setFormData] = useState({
    title: init.title || '',
    answerTemplateId: init.answerTemplateId || '',
  });

  useEffect(() => {
    setFormData({
      title: init.title || '',
      answerTemplateId: init.answerTemplateId || '',
    });
    //eslint-disable-next-line
  }, [init]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAutocompleteChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      answerTemplateId: newValue ? newValue._id : '',
    }));
  };

  const handleSave = () => {
    const dataToSave = {
      ...formData,
      _id: init._id || undefined,
    };
    if (isEditMode) {
      onEdit(dataToSave);
    } else {
      onSave(dataToSave);
    }
    setFormData({ title: '', answerTemplateId: '' });
    onClose();
  };

  return (
    <RoundedDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
    >
      <DialogTitle>{`${isEditMode ? 'Edit' : 'Create'} QA`}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Title */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              variant="outlined"
              required
            />
          </Grid>

          {/* Answer Template с Autocomplete */}
          <Grid item xs={12}>
            <Autocomplete
              options={templates}
              getOptionLabel={(option) => option.title || ''}
              value={templates.find((t) => t._id === formData.answerTemplateId) || null}
              onChange={handleAutocompleteChange}
              filterOptions={(options, { inputValue }) =>
                options.filter(
                  (option) =>
                    option.title.toLowerCase().includes(inputValue.toLowerCase()) ||
                    (option.template && option.template.toLowerCase().includes(inputValue.toLowerCase())),
                )
              }
              renderOption={(props, option) => (
                <li {...props} style={{ maxWidth: '600px' }}>
                  <div>
                    <Typography variant="body1">{option.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.template || 'No description'}
                    </Typography>
                  </div>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Answer Template" variant="outlined" required />}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <StandardDialogActions onClose={onClose} onDone={handleSave} />
    </RoundedDialog>
  );
}
